import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@evergis/ui";

import { CheckboxContainer } from "../styled";
import { CheckboxDescription, TextLine } from "../../DeleteLayer/styled";
import { UsersTooltip } from "../../DeleteLayer/UsersTooltip";
import { PUBLIC_ROLE } from "../../../../hooks/acl/publicAccess";
import { isEditable } from "../../../../evergis/core/permissions";
import { getCurrentTab } from "../../../../ducks/location/selectors";
import { getUsername } from "../../../../ducks/user";
import { RequestDependencyInfo, RequestInfoType } from "./RequestInfo";
import { CheckedDeleteResourcesState, ToggleDeleteResources } from "../hooks/useDeleteResourcesChoice";
import { ResourcesResolved } from "../resolveResources";

type DeleteProjectsContentProps = {
  checked: CheckedDeleteResourcesState;
  toggle: ToggleDeleteResources;
} & ResourcesResolved;

export const DeleteLayersContent: FC<DeleteProjectsContentProps> = ({ projects, layers, tables, checked, toggle }) => {
  const { t } = useTranslation("dialogs");
  const locationTab = useSelector(getCurrentTab);
  const showSourceCheckbox = tables.length > 0;
  const canDeleteFromCatalog = !locationTab && isEditable(layers[0].permissions);

  const currentUser: string = useSelector(getUsername);
  const usersWithLayerAccessNames = useMemo(
    () =>
      canDeleteFromCatalog && layers.length
        ? layers[0].acl.data
            .filter(({ role }) => role !== `__${currentUser}`)
            .reduce(
              (acc, { role }) =>
                role === PUBLIC_ROLE ? [t("access.public"), ...acc] : [...acc, role.replace(/^(__)/, "")],
              [],
            )
        : null,
    [canDeleteFromCatalog, currentUser, layers, t],
  );

  return (
    <>
      {canDeleteFromCatalog && (
        <>
          <CheckboxContainer>
            <Checkbox checked={checked.layers} label={t("deleteLayer.deleteFromCatalog")} onChange={toggle.layers} />
          </CheckboxContainer>
          {usersWithLayerAccessNames.length > 0 && (
            <CheckboxDescription>
              <TextLine>
                <span>{t("deleteLayer.usersWithLayerAccess")}:&nbsp;</span>
                <UsersTooltip users={usersWithLayerAccessNames} />
              </TextLine>
              <TextLine>
                <span>{t("deleteLayer.unavailableCatalog")}</span>
              </TextLine>
            </CheckboxDescription>
          )}
        </>
      )}
      {showSourceCheckbox && (
        <>
          <CheckboxContainer>
            <Checkbox
              checked={checked.tables}
              label={t("deleteResources.deleteDataSources")}
              onChange={toggle.tables}
            />
          </CheckboxContainer>
          <RequestDependencyInfo
            type={RequestInfoType.MapsWithTables}
            projects={projects}
            layers={layers}
            tables={tables}
          />
          <RequestDependencyInfo
            type={RequestInfoType.LayersWithTables}
            projects={projects}
            layers={layers}
            tables={tables}
          />
        </>
      )}
    </>
  );
};
